import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNowTime } from "lib/hooks/useNowTime";

import AdminKpiGrid from "components/admin/AdminKpiGrid";
import AdminActivityCard from "components/admin/AdminActivityCard";
import AdminManagerRequestCard from "components/admin/AdminManagerRequestCard";
import AdminUploadCard from "components/admin/AdminUploadCard";
import Spinner from "components/Spinner";

import { getAdminDashboard } from "lib/api/getAdminDashboard";
import {
  approveManagerCreationRequest,
  getManagerCreationRequests,
  rejectManagerCreationRequest,
} from "lib/api/roleAdmin";
import { useAppStore } from "lib/store/useAppStore";

export default function AdminHomePage() {
  const time = useNowTime();
  const nav = useNavigate();
  const showAlert = useAppStore((s) => s.showAlert);

  const [kpis, setKpis] = useState([]);
  const [activities, setActivities] = useState([]);
  const [uploadStats7d, setUploadStats7d] = useState({});
  const [managerRequests, setManagerRequests] = useState([]);
  const [managerRequestError, setManagerRequestError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const applyDashboardResult = useCallback((result) => {
    const nextKpis = Array.isArray(result.kpis) ? result.kpis : [];
    setKpis(
      nextKpis.map((k, idx) => ({
        ...k,
        id: `kpi-${idx + 1}`,
        to: `/admin/kpi/${idx + 1}`,
      })),
    );
    setActivities(Array.isArray(result.activities) ? result.activities : []);
    setUploadStats7d(result.uploadStats7d ?? {});
  }, []);

  const applyManagerRequestResult = useCallback((result) => {
    if (result.success) {
      setManagerRequests(result.data?.requests ?? []);
      setManagerRequestError("");
      return;
    }

    setManagerRequests([]);
    setManagerRequestError(
      result.message || "매니저 생성 요청을 불러오지 못했습니다.",
    );
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      setIsLoading(true);
      const [dashboardResult, managerRequestResult] = await Promise.all([
        getAdminDashboard(),
        getManagerCreationRequests({ status: "pending" }),
      ]);

      if (!alive) return;

      applyManagerRequestResult(managerRequestResult);

      if (!dashboardResult?.success) {
        nav("/", { replace: true });
        setIsLoading(false);
        return;
      }

      applyDashboardResult(dashboardResult);
      setIsLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [applyDashboardResult, applyManagerRequestResult, nav]);

  const refreshManagerRequests = async () => {
    const result = await getManagerCreationRequests({ status: "pending" });
    applyManagerRequestResult(result);
  };

  const refreshDashboard = async () => {
    const result = await getAdminDashboard();
    if (result?.success) {
      applyDashboardResult(result);
    }
  };

  const handleReviewManagerRequest = async (id, action) => {
    const result =
      action === "approve"
        ? await approveManagerCreationRequest(id)
        : await rejectManagerCreationRequest(id);

    showAlert({
      text: result.message || "매니저 생성 요청을 처리했습니다.",
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      await refreshManagerRequests();
      await refreshDashboard();
    }
  };

  return (
    <section className="content admin roleAdminPage adminRootPage">
      <div className="roleAdminHeader">
        <div>
          <h2>관리자 대시보드</h2>
          <p>{time}</p>
        </div>
      </div>

      <AdminKpiGrid kpis={kpis} />

      {managerRequests.length > 0 ? (
        <AdminManagerRequestCard
          managerRequests={managerRequests}
          managerRequestError={managerRequestError}
          onReview={handleReviewManagerRequest}
        />
      ) : null}

      <div className="mainGrid">
        <AdminActivityCard activities={activities} moreTo="/admin/logs" />

        <AdminUploadCard
          title="파일 업로드 현황"
          total="-"
          trendLabel=""
          uploadStats7d={uploadStats7d}
          moreTo="/admin/files"
        />
      </div>

      {isLoading && <Spinner />}
    </section>
  );
}
